import { useEffect, useState } from "react";
import { settings } from "app";
import { useNavigate } from "react-router-dom";

import {
  List,
  TextField,
  SimpleList,
  Datagrid,
  useNotify,
  useDataProvider,
  Title,
  FunctionField,
  ReferenceField,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  useStore,
} from "react-admin";
import { Box, Typography, Paper, useMediaQuery, Button } from "@mui/material";
import NotesIcon from "@mui/icons-material/Notes";

import { MOBILE_BREAKPOINT } from "utils/constants";
import { candidateRequests } from "api/candidateApi";

import { EmptyList } from "server/components";
import CustomFilter from "features/CustomFilter";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { administrationRequests } from "api/administrationApi";
import { useNotifyError } from "utils/useNotifyError";
import { MobileListRow } from "features/MobileListRow";

const preparatoryFilters = [
  <TextInput
    label="Име"
    source="firstName"
    alwaysOn
  />,
  <TextInput
    label="Презиме"
    source="secondName"
    alwaysOn
  />,
  <TextInput
    label="Фамилия"
    source="lastName"
    alwaysOn
  />,
  <ReferenceInput
    source="residenceCountryId"
    reference={administrationRequests.getCountriesSelect()}
    alwaysOn
  >
    <AutocompleteInput
      label="Държава"
      optionText="nameBg"
      optionValue="id"
      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
    />
  </ReferenceInput>,
];
const ListAcceptedPreparatory = () => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const dataProvider = useDataProvider();

  const [campaignChoices, setCmapaignChoices] = useState([]);
  const [citizenshipId, setCitizenId] = useState(null);

  const STORE_KEY = "campaignId-query-preparatory";

  // filters drives the <List>, the gate and the citizenship fetch (persisted via useStore);
  // campaignIds is the draft selection bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const [campaignIds, setCampaignIds] = useState(filters?.campaignId ?? null);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const { data } = await dataProvider.customRequest(
          candidateRequests.getCampaignsSelect(),
          null,
          "GET",
          `?CitizenshipIds=2&CitizenshipIds=3`,
        );
        setCmapaignChoices(data);
      } catch (error) {
        notifyError(error);
      }
    };
    fetchCampaigns();
  }, [dataProvider, notifyError]);

  useEffect(() => {
    if (!filters?.campaignId) return;

    const fetchCampaignCitizenship = async () => {
      try {
        const { data } = await dataProvider.customRequest(candidateRequests.getCampaignCitizenship(filters.campaignId));
        setCitizenId(data[0]);
      } catch (err) {
        notifyError(err);
      }
    };
    fetchCampaignCitizenship();
  }, [dataProvider, notifyError, filters?.campaignId]);

  const searchParams = () => {
    if (campaignIds) {
      setFilters({ campaignId: campaignIds });
    } else {
      notify("Моля въведете кампания!", { type: "error" });
    }
  };

  return (
    <>
      {!filters && <Title title="Списък кандидатури за подготвителна година" />}
      <CustomFilter
        title="Списък кандидатури за подготвителна година"
        filters={[
          {
            label: "Кампания",
            value: campaignIds,
            setValue: setCampaignIds,
            type: "autocomplete",
            multiple: false,
            choices: campaignChoices,
          },
        ]}
        cardWidth={isMobile ? "350px" : "700px"}
        buttonLabel="Провери"
        buttonOnClick={searchParams}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене на студенти.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <>
          <List
            filters={preparatoryFilters}
            title="Списък кандидатури за подготвителна година"
            sx={settings.style.blockStyle}
            resource={candidateRequests.preparatoryYearCandidates()}
            filter={{ campaignId: filters.campaignId }}
            empty={<EmptyList />}
            exporter={false}
          >
            {isMobile ? (
              <SimpleList
                empty={<EmptyList />}
                linkType={false}
                sx={settings.style.mobileList}
                primaryText={record => (
                  <MobileListRow
                    fields={[
                      {
                        label: "Имена",
                        value: (
                          <>
                            {record.firstName} {record.secondName} {record.lastName}
                          </>
                        ),
                      },
                    ]}
                  />
                )}
                secondaryText={record => (
                  <div>
                    <MobileListRow
                      fields={[
                        {
                          label: "Специалност",
                          value: (
                            <ReferenceField
                              source="specialityId"
                              reference={administrationRequests.getSpecialitiesSelect()}
                              label="Специалност"
                            />
                          ),
                        },
                      ]}
                    />
                    <MobileListRow
                      fields={[
                        {
                          label: "Държава",
                          value: (
                            <ReferenceField
                              source="countryId"
                              reference={administrationRequests.getCountriesSelect()}
                              label="Държава"
                            >
                              <TextField source="nameBg" />
                            </ReferenceField>
                          ),
                        },
                      ]}
                    />
                    <SecuredComponent permission={permissions.canEnrollPreparatoryYearCandidates}>
                      <EnrollingButton
                        label="Запиши"
                        specialityId={record.specialityId}
                        candidacyId={record.candidacyId}
                        citizenshipId={citizenshipId}
                      />
                    </SecuredComponent>
                  </div>
                )}
              />
            ) : (
              <Datagrid
                bulkActionButtons={false}
                rowClick={false}
                sx={settings.style.firstExpandIconDatagrid}
              >
                <TextField
                  label="Име"
                  source="firstName"
                  sortable={false}
                />
                <TextField
                  label="Презиме"
                  source="secondName"
                  sortable={false}
                />
                <TextField
                  label="Фамилия"
                  source="lastName"
                  sortable={false}
                />
                <ReferenceField
                  source="specialityId"
                  reference={administrationRequests.getSpecialitiesSelect()}
                  label="Специалност"
                />
                <ReferenceField
                  source="countryId"
                  reference={administrationRequests.getCountriesSelect()}
                  label="Държава"
                >
                  <TextField source="nameBg" />
                </ReferenceField>

                <SecuredComponent permission={permissions.canEnrollPreparatoryYearCandidates}>
                  <FunctionField
                    label=""
                    render={record => (
                      <EnrollingButton
                        specialityId={record.specialityId}
                        candidacyId={record.candidacyId}
                        citizenshipId={citizenshipId}
                      />
                    )}
                  />
                </SecuredComponent>
              </Datagrid>
            )}
          </List>
        </>
      )}
    </>
  );
};
export default ListAcceptedPreparatory;
const EnrollingButton = ({ candidacyId, specialityId, citizenshipId }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/candidate/preparatory-deos`, {
      state: {
        candidacyId: candidacyId,
        specialityId: specialityId,
        citizenshipId: citizenshipId,
      },
    });
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<NotesIcon />}
      onClick={handleClick}
    >
      Запиши
    </Button>
  );
};
